import React from "react";
import { Link } from "react-router-dom";
import { useApp } from "../contexts/AppContext";
import { useCart } from "../contexts/CartContext";
import { ShoppingBag } from "lucide-react";

export default function ProductCard({ product, artistName, artistSlug, artistId, badge }) {
  const { formatPrice, t, pickField } = useApp();
  const { add } = useCart();

  const discountPct = Math.max(0, Math.min(90, Number(product.discount_pct) || 0));
  const hasDiscount = discountPct > 0;
  const finalPrice = hasDiscount
    ? Number(product.price_azn) * (1 - discountPct / 100)
    : Number(product.price_azn);

  const title = pickField(product, "title") || product.title;

  // YENİ badge auto-expires 2 weeks (14 days) after the admin flagged the
  // product as a "Yeni gələn". We rely on `new_arrival_set_at` which is
  // written by AdminProducts the moment the checkbox is turned ON. Seeded
  // or imported products (which never had the checkbox toggled by a human)
  // don't carry this timestamp and therefore won't show the YENİ badge.
  const isFreshlyNew = (() => {
    if (!product.new_arrival) return false;
    const setAt = product.new_arrival_set_at;
    if (!setAt) return false;
    let setAtMs = null;
    if (typeof setAt.toMillis === "function") setAtMs = setAt.toMillis();
    else if (typeof setAt.toDate === "function") setAtMs = setAt.toDate().getTime();
    else if (setAt.seconds) setAtMs = setAt.seconds * 1000;
    else setAtMs = new Date(setAt).getTime();
    if (!setAtMs || Number.isNaN(setAtMs)) return false;
    const TWO_WEEKS_MS = 14 * 24 * 60 * 60 * 1000;
    return (Date.now() - setAtMs) <= TWO_WEEKS_MS;
  })();

  const showBadge = badge || (
    product.is_used ? (t.product.secondHand || "İKİNCİ ƏL")
    : product.limited ? t.product.limited
    : isFreshlyNew ? t.product.newArrival
    : null
  );

  return (
    <div className="group" data-testid={`product-card-${product.slug}`}>
      <div className="zoom-wrap card-flat relative aspect-square overflow-hidden">
        <Link to={`/product/${product.slug}`}>
          <img
            src={product.cover_url}
            alt={title}
            width="400"
            height="400"
            loading="lazy"
            decoding="async"
            className="w-full h-full object-cover"
          />
        </Link>
        {hasDiscount && (
          <div
            className="absolute top-3 right-3 metadata bg-[var(--accent)] text-white px-2 py-1"
            data-testid={`product-discount-${product.slug}`}
          >
            -{discountPct}%
          </div>
        )}
        {showBadge && (
          <div className="absolute top-3 left-3 metadata bg-white text-black px-2 py-1" data-testid={`product-badge-${product.slug}`}>{showBadge}</div>
        )}
        <button
          onClick={(e) => { e.preventDefault(); add({ ...product, artist_name: artistName }); }}
          className="absolute bottom-3 right-3 w-11 h-11 rounded-full bg-black text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition"
          aria-label={t.common.addToCart}
          data-testid={`product-add-${product.slug}`}
        >
          <ShoppingBag size={18} />
        </button>
      </div>
      <div className="mt-4 flex items-start justify-between gap-2">
        <div className="min-w-0">
          {artistName ? (
            (artistId || artistSlug) ? (
              <Link
                to={`/artist/${artistId || artistSlug}`}
                onClick={(e) => e.stopPropagation()}
                className="metadata text-[var(--text-muted)] hover:text-[var(--primary)] transition-all duration-300 inline-block origin-left group-hover:scale-110 group-hover:text-[var(--text)]"
                data-testid={`product-artist-${product.slug}`}
              >
                {artistName}
              </Link>
            ) : (
              <div className="metadata text-[var(--text-muted)] transition-all duration-300 inline-block origin-left group-hover:scale-110 group-hover:text-[var(--text)]">{artistName}</div>
            )
          ) : (
            <div className="metadata text-[var(--text-muted)]"></div>
          )}
          <Link to={`/product/${product.slug}`} className="block">
            <h3 className="product-title text-[15px] md:text-[17px] mt-1 hover:text-[var(--primary)] transition-colors">{title}</h3>
          </Link>
        </div>
        <div className="text-right flex-shrink-0" data-testid={`product-price-${product.slug}`}>
          {hasDiscount ? (
            <>
              <div className="font-mono text-xs text-[var(--text-muted)] line-through leading-none">
                {formatPrice(product.price_azn)}
              </div>
              <div className="font-mono text-sm text-[var(--accent)] mt-1 font-medium">
                {formatPrice(finalPrice)}
              </div>
            </>
          ) : (
            <div className="font-mono text-sm text-[var(--text)]">{formatPrice(product.price_azn)}</div>
          )}
        </div>
      </div>
    </div>
  );
}
